/**
 * Phone calls to customers and leads (Lj, Oct 11: "track phone calls to people so we can use that for sales goals —
 * e.g. 'call 5 customers/leads this week'"). The phone's call log is read on the device (modules/ops-calls, Android,
 * READ_CALL_LOG) and every row is matched HERE, on the device, against the customer list (opsCustomers — customers and
 * Bids leads both live there) by phone number. Only matched calls are kept:
 *   calls/{c<startedAt>_<uid>} = { customerId, direction: out|in|missed, startedAt, durationSec, dateLocal, uid }
 * Calls to anyone else become one count per day — calls/other_<uid>_<date> = { kind: 'other', count, out, in, missed,
 * seconds } — how many, never which numbers. No numbers and no contact names ever leave the phone. Pure — no I/O.
 */
import { localDateStr } from './time.js';

const DAY = 86400000;

// ── numbers ─────────────────────────────────────────────────────────────────────────────────────────────────

/** A phone number's digits, a North American leading 1 dropped (battlebids intake.ts phoneKey — the Bids de-dupe). */
export function phoneKey(v) {
  const d = String(v ?? '').replace(/\D/g, '');
  return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
}

/**
 * E.164 for a number: North American 10 digits (or 1 + 10) → +1XXXXXXXXXX; a number written with a leading + keeps its
 * digits (8–15); anything else (short codes, 7-digit local numbers, blanks) → null.
 */
export function phoneE164(v) {
  const raw = String(v ?? '').trim();
  if (!raw) return null;
  const d = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) return d.length >= 8 && d.length <= 15 ? `+${d}` : null;
  const k = phoneKey(raw);
  return k.length === 10 ? `+1${k}` : null;
}

/** The key two numbers are compared on: the E.164's national part for +1, else the digits (7+ or nothing). */
export function matchKey(v) {
  const e = phoneE164(v);
  if (e && e.startsWith('+1') && e.length === 12) return e.slice(2);
  const k = e ? e.slice(1) : phoneKey(v);
  return k.length >= 7 ? k : null;
}

const DIRECTION = { out: 'out', outgoing: 'out', in: 'in', incoming: 'in', answered_externally: 'in', missed: 'missed', rejected: 'missed', blocked: 'missed', voicemail: 'missed' };
/** A call-log type → out | in | missed (null = not a call we count). */
export const callDirection = (t) => DIRECTION[String(t ?? '').toLowerCase()] || null;

// ── customers and leads ─────────────────────────────────────────────────────────────────────────────────────

/** Every number a customer doc may carry (the books publish `phone`; `phones` / `mobile` / `altPhone` if they ever come). */
export function customerPhones(c) {
  if (!c) return [];
  const list = [c.phone, c.mobile, c.altPhone, c.phone2, ...(Array.isArray(c.phones) ? c.phones : [])];
  return list.map((p) => (p && typeof p === 'object' ? p.number || p.value : p)).filter((p) => typeof p === 'string' && p.trim());
}

/** match key → customer. Two customers on one number: the one listed first keeps it (`shared` counts them). */
export function customerPhoneIndex(customers = []) {
  const index = new Map();
  let shared = 0;
  for (const c of customers || []) {
    if (!c || !c.id || c.deletedAt || c.archived) continue;
    for (const p of customerPhones(c)) {
      const k = matchKey(p);
      if (!k) continue;
      if (index.has(k)) { if (index.get(k).id !== c.id) shared += 1; continue; }
      index.set(k, c);
    }
  }
  return Object.assign(index, { shared });
}

/** The customer a call-log row is with, or null. */
export function matchCall(row, index) {
  if (!row || !index) return null;
  for (const v of [row.normalized, row.number]) {
    const k = matchKey(v);
    if (k && index.has(k)) return index.get(k);
  }
  return null;
}

/**
 * Lead or customer. Bids keeps a lead's place on the customer (`status`: new → contacted → assessment → quoted → won /
 * lost, bids 0.5.48) and the books mirror it to opsCustomers with `firstTouchSrc` and `leadSince` (battlebooks
 * customerForOps, v0.1.252). An open stage (new / contacted / assessment / quoted) = Lead; won, no status, or someone
 * who already has work with us (`withWork`: a project of theirs linked to a books job past the lead stage — a repeat
 * customer's new enquiry puts them back to `new`) = Customer. `lost` stays a lead (a lost lead never bought).
 * Before the books deploy no customer carries the fields and everyone reads as Customer (`leadStatusKnown` says so).
 */
export const OPEN_LEAD_STAGES = Object.freeze(['new', 'contacted', 'assessment', 'quoted']);
export const LEAD_STAGES = Object.freeze([...OPEN_LEAD_STAGES, 'lost']);
/** The Bids stage on a customer (new … quoted, won, lost) or null. */
export const leadStage = (c) => {
  const st = String(c?.status || '').trim().toLowerCase();
  return st === 'won' || LEAD_STAGES.includes(st) ? st : null;
};
export const personKind = (c, withWork = null) => {
  if (!c || (typeof withWork?.has === 'function' && c.id && withWork.has(c.id))) return 'customer';
  return LEAD_STAGES.includes(leadStage(c)) ? 'lead' : 'customer';
};
/** The mirror carries the fields (status may be null for a plain customer) — or any customer has a stage. */
export const leadStatusKnown = (customers = []) => (customers || []).some((c) => c && ('leadSince' in c || 'firstTouchSrc' in c || (typeof c.status === 'string' && c.status)));

/** Books job statuses that are still a lead, not work (battlebooks lib/leadProject.ts LEAD_PROJECT_STATUSES). */
const LEAD_JOB_STATUSES = new Set(['cold', 'contacted', 'draft', 'estimate', 'proposed']);
/**
 * Customers who already have work: an ops project of theirs linked to a books job (opsJobs) past the lead stage.
 * A linked job not on the device yet doesn't count. → Set of customer ids.
 */
export function customersWithWork(projects = [], jobs = []) {
  const jobBy = new Map((jobs || []).filter(Boolean).map((j) => [j.projectId || j.id, j]));
  const out = new Set();
  for (const p of projects || []) {
    if (!p || p.deletedAt || !p.customerId || !p.bookProjectId) continue;
    const j = jobBy.get(p.bookProjectId);
    if (j && !j.deletedAt && !LEAD_JOB_STATUSES.has(String(j.status || ''))) out.add(p.customerId);
  }
  return out;
}

const STAGE_LABEL = { new: 'New lead', contacted: 'Contacted', assessment: 'Assessment', quoted: 'Quoted', lost: 'Lost lead' };
/** The customer card's small pill: { stage, label, open } for a lead stage; null for a customer / no stage. */
export function leadPill(c) {
  const st = leadStage(c);
  return st && STAGE_LABEL[st] ? { stage: st, label: STAGE_LABEL[st], open: OPEN_LEAD_STAGES.includes(st) } : null;
}

// ── the call log → docs ─────────────────────────────────────────────────────────────────────────────────────

export const callDocId = (uid, startedAt) => `c${Math.round(Number(startedAt) || 0)}_${uid || 'me'}`;
export const otherCallsId = (uid, date) => `other_${uid || 'me'}_${date}`;
export const isOtherCalls = (d) => !!d && d.kind === 'other';

/** Local midnight of the day holding `ms`. */
const dayStart = (ms) => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };

/**
 * Where the next read of the call log starts: the start of the watermark's day — so that day's "other" count is
 * rebuilt whole and a re-read never double-counts — or `firstDays` back the first time (a month goal has its month).
 */
export function callLogSince(watermark, now = Date.now(), firstDays = 35) {
  const w = Number(watermark);
  if (Number.isFinite(w) && w > 0) return dayStart(Math.min(w, now));
  return dayStart(now - firstDays * DAY);
}

const cleanRow = (r) => {
  const startedAt = Number(r?.startedAt ?? r?.date);
  const direction = callDirection(r?.type ?? r?.direction);
  if (!Number.isFinite(startedAt) || startedAt <= 0 || !direction) return null;
  const durationSec = direction === 'missed' ? 0 : Math.max(0, Math.round(Number(r.durationSec ?? r.duration) || 0));
  return { number: r.number ?? null, normalized: r.normalized ?? null, direction, startedAt: Math.round(startedAt), durationSec };
};

/**
 * Call-log rows (read from `since`, a day boundary) → what to keep.
 * rows: [{ number, normalized?, type: in|out|missed|…, startedAt (ms), durationSec }]
 * → { matched: [{ id, doc }], other: [{ id, doc }], latest, read, kept }
 * Every day from `since` to today gets an "other" doc (a zero day included, so a day once counted and now 0 is fixed).
 */
export function summariseCallLog(rows = [], customers = [], { uid, since = 0, now = Date.now() } = {}) {
  const index = customerPhoneIndex(customers);
  const matched = []; const days = new Map(); let latest = 0; let read = 0;
  const blank = () => ({ count: 0, out: 0, in: 0, missed: 0, seconds: 0 });
  if (since > 0) for (let t = dayStart(since); t <= now; t += DAY) days.set(localDateStr(t + 12 * 3600000), blank());
  for (const raw of rows || []) {
    const r = cleanRow(raw);
    if (!r || r.startedAt < since || r.startedAt > now + 60000) continue;
    read += 1;
    latest = Math.max(latest, r.startedAt);
    const dateLocal = localDateStr(r.startedAt);
    const c = matchCall(r, index);
    if (c) {
      matched.push({ id: callDocId(uid, r.startedAt), doc: { customerId: c.id, direction: r.direction, startedAt: r.startedAt, durationSec: r.durationSec, dateLocal, uid: uid || null } });
      continue;
    }
    const d = days.get(dateLocal) || blank();
    d.count += 1; d[r.direction] += 1; d.seconds += r.durationSec;
    days.set(dateLocal, d);
  }
  const other = [...days].sort(([a], [b]) => (a < b ? -1 : 1)).map(([date, d]) => ({ id: otherCallsId(uid, date), doc: { kind: 'other', dateLocal: date, uid: uid || null, ...d } }));
  return { matched, other, latest, read, kept: matched.length };
}

const SAME_KEYS = ['customerId', 'direction', 'startedAt', 'durationSec', 'dateLocal', 'kind', 'count', 'out', 'in', 'missed', 'seconds'];
const same = (a, b) => !!a && SAME_KEYS.every((k) => (a[k] ?? null) === (b[k] ?? null));

/**
 * The writes for a summary, given the calls already on the device (no churn: a doc that's already right isn't
 * rewritten; a zero "other" day that was never stored isn't written). → [{ op: 'set', path, data }]
 */
export function callWrites(summary, existing = [], { now = Date.now() } = {}) {
  const have = new Map((existing || []).map((d) => [d.id, d]));
  const out = [];
  for (const { id, doc } of [...(summary?.matched || []), ...(summary?.other || [])]) {
    const cur = have.get(id);
    if (same(cur, doc)) continue;
    if (!cur && doc.kind === 'other' && !doc.count) continue;
    out.push({ op: 'set', path: `calls/${id}`, data: { ...doc, createdAt: cur?.createdAt || now, updatedAt: now } });
  }
  return out;
}

// ── reading them back ───────────────────────────────────────────────────────────────────────────────────────

const live = (d) => d && !d.deletedAt;
/** Matched calls (not the "other" counts), newest first; optional customer / dates (YYYY-MM-DD, inclusive) / direction. */
export function callsFor(calls = [], { customerId = null, from = null, to = null, direction = null } = {}) {
  return (calls || []).filter((c) => live(c) && !isOtherCalls(c) && c.customerId
    && (!customerId || c.customerId === customerId) && (!from || c.dateLocal >= from) && (!to || c.dateLocal <= to)
    && (!direction || c.direction === direction))
    .sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
}

/** The "other" (not a customer) counts over a span → { count, out, in, missed, seconds }. */
export function otherCalls(calls = [], { from = null, to = null } = {}) {
  const t = { count: 0, out: 0, in: 0, missed: 0, seconds: 0 };
  for (const c of calls || []) {
    if (!live(c) || !isOtherCalls(c) || (from && c.dateLocal < from) || (to && c.dateLocal > to)) continue;
    for (const k of Object.keys(t)) t[k] += Number(c[k]) || 0;
  }
  return t;
}

/** The last call with a customer (any direction), or null. */
export const lastCallFor = (calls, customerId) => (customerId ? callsFor(calls, { customerId })[0] || null : null);

/** "4 min" · "<1 min" · "1 h 5 min". */
export function formatCallMinutes(sec) {
  const s = Math.max(0, Number(sec) || 0);
  if (s < 60) return s > 0 ? '<1 min' : '0 min';
  const m = Math.round(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`;
}

const WEEKDAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** When, said short: Today · Yesterday · Tue (this past week) · Oct 3. */
export function callDayLabel(ms, now = Date.now()) {
  const d = localDateStr(ms); const today = localDateStr(now);
  if (d === today) return 'Today';
  if (d === localDateStr(dayStart(now) - DAY / 2)) return 'Yesterday';
  const t = new Date(ms);
  if (dayStart(now) - dayStart(ms) < 6 * DAY && ms < now) return WEEKDAY[t.getDay()];
  return `${MONTH[t.getMonth()]} ${t.getDate()}`;
}

/** "Last call · Tue · 4 min" (incoming: "· in"; missed: "· missed"); null when there's none. */
export function lastCallText(call, now = Date.now()) {
  if (!call) return null;
  const how = call.direction === 'missed' ? 'missed' : `${call.direction === 'in' ? 'in · ' : ''}${formatCallMinutes(call.durationSec)}`;
  return `Last call · ${callDayLabel(call.startedAt, now)} · ${how}`;
}

/**
 * Calls grouped by person over a span → [{ customerId, name, kind, out, in, missed, seconds, outSeconds, last, lastOut }],
 * most outgoing first. `kind` = lead | customer (personKind; `withWork` = customersWithWork, so a repeat customer stays one).
 */
export function callsByCustomer(calls = [], customers = [], { from = null, to = null, withWork = null } = {}) {
  const byId = new Map((customers || []).map((c) => [c.id, c]));
  const map = new Map();
  for (const c of callsFor(calls, { from, to })) {
    const p = byId.get(c.customerId);
    const g = map.get(c.customerId) || { customerId: c.customerId, name: p?.name || 'A customer no longer listed', kind: personKind(p, withWork), out: 0, in: 0, missed: 0, seconds: 0, outSeconds: 0, last: 0, lastOut: 0 };
    g[c.direction] = (g[c.direction] || 0) + 1;
    g.seconds += Number(c.durationSec) || 0;
    if (c.direction === 'out') { g.outSeconds += Number(c.durationSec) || 0; g.lastOut = Math.max(g.lastOut, c.startedAt || 0); }
    g.last = Math.max(g.last, c.startedAt || 0);
    map.set(c.customerId, g);
  }
  return [...map.values()].sort((a, b) => b.out - a.out || b.seconds - a.seconds || b.last - a.last);
}

/**
 * A day's calls for the Daily Log → { count, minutes, seconds, matched, other, people: callsByCustomer rows } —
 * count = every call that day (matched + other, missed included), minutes = talk time.
 */
export function dayCalls(calls = [], customers = [], date, { withWork = null } = {}) {
  const people = callsByCustomer(calls, customers, { from: date, to: date, withWork });
  const matched = callsFor(calls, { from: date, to: date });
  const other = otherCalls(calls, { from: date, to: date });
  const seconds = matched.reduce((a, c) => a + (Number(c.durationSec) || 0), 0) + other.seconds;
  return { count: matched.length + other.count, matched: matched.length, other: other.count, seconds, minutes: Math.round(seconds / 60), people };
}

/** A tel: link for a customer's first number (digits and a leading +), or null. */
export function telLink(c) {
  const p = customerPhones(c)[0];
  if (!p) return null;
  const e = phoneE164(p);
  const d = e || p.replace(/[^\d+]/g, '');
  return d ? `tel:${d}` : null;
}
